import type { ReactNode } from "react";
import { sketchFont } from "./font";
import {
  hachure,
  type Point,
  roughArrow,
  roughBracket,
  roughEllipse,
  roughLine,
  roughRect,
  roughUnderline,
  seeded,
} from "./geometry";
import styles from "./sketch.module.css";

/*
 * Hand-drawn notes and figures for case studies and articles. All of it
 * renders on the server as SVG and real text: no client script, the words
 * stay selectable and searchable, and each figure carries a text
 * description for screen readers.
 */

const ROOT = `${sketchFont.variable} ${styles.ink}`;
const PEN = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" } as const;

/** Caveat runs about 0.44em per character at these sizes. */
const handWidth = (text: string, size: number) => text.length * size * 0.44;

/* ---------------------------------------------------------------- */
/*  Figure frame and canvas                                          */
/* ---------------------------------------------------------------- */

/** A numbered sketch on ruled paper, captioned like the site's figures. */
export function SketchFigure({
  number,
  caption,
  children,
  className,
}: {
  number?: string;
  caption: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <figure className={`${ROOT} ${styles.figure} ${className ?? ""}`}>
      <div className={styles.paper}>{children}</div>
      <figcaption className={styles.caption}>
        {number ? `Figure ${number}: ` : null}
        {caption}
      </figcaption>
    </figure>
  );
}

/** The drawing surface. `title` and `description` are what a screen reader
 *  hears in place of the drawing, so describe what it shows. */
export function SketchCanvas({
  width,
  height,
  title,
  description,
  children,
  id: idSource,
}: {
  width: number;
  height: number;
  title: string;
  description: string;
  children: ReactNode;
  /** Distinguishes two drawings with the same title on one page. */
  id?: string;
}) {
  const id = `sketch-${(idSource ?? title).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={styles.canvas}
      role="img"
      aria-labelledby={`${id}-title ${id}-desc`}
    >
      <title id={`${id}-title`}>{title}</title>
      <desc id={`${id}-desc`}>{description}</desc>
      {children}
    </svg>
  );
}

/* ---------------------------------------------------------------- */
/*  Primitives (inside SketchCanvas)                                  */
/* ---------------------------------------------------------------- */

/** A box, optionally hatched, with a handwritten label in the middle. */
export function SketchBox({
  x,
  y,
  width,
  height,
  label,
  hatched = false,
  size = 24,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
  hatched?: boolean;
  size?: number;
}) {
  const random = seeded(`box:${label ?? ""}:${x}:${y}:${width}:${height}`);
  const labelWidth = label ? handWidth(label, size) + 14 : 0;
  return (
    <g>
      {hatched ? (
        <path d={hachure(x, y, width, height, random)} {...PEN} strokeWidth={1.1} opacity={0.75} />
      ) : null}
      <path d={roughRect(x, y, width, height, random)} {...PEN} />
      {label ? (
        <>
          {/* Clear the hatching behind the word, as a hand writes over a gap. */}
          {hatched ? (
            <rect
              x={x + width / 2 - labelWidth / 2}
              y={y + height / 2 - size * 0.55}
              width={labelWidth}
              height={size * 1.1}
              fill="var(--sketch-paper)"
            />
          ) : null}
          <text x={x + width / 2} y={y + height / 2 + size * 0.32} fontSize={size} textAnchor="middle">
            {label}
          </text>
        </>
      ) : null}
    </g>
  );
}

/** A handwritten word, optionally with a loop drawn round it. */
export function SketchLabel({
  x,
  y,
  children,
  circled = false,
  size = 30,
  anchor = "start",
}: {
  x: number;
  y: number;
  children: string;
  circled?: boolean;
  size?: number;
  anchor?: "start" | "middle";
}) {
  const width = handWidth(children, size);
  const cx = anchor === "middle" ? x : x + width / 2;
  const random = seeded(`label:${children}:${x}:${y}`);
  return (
    <g>
      {circled ? (
        <path
          d={roughEllipse(cx, y - size * 0.3, width / 2 + size * 0.7, size * 0.74, random)}
          {...PEN}
        />
      ) : null}
      <text x={x} y={y} fontSize={size} textAnchor={anchor}>
        {children}
      </text>
    </g>
  );
}

export function SketchArrow({ from, to, curve }: { from: Point; to: Point; curve?: number }) {
  const random = seeded(`arrow:${from.join(",")}:${to.join(",")}`);
  return <path d={roughArrow(from, to, random, curve)} {...PEN} />;
}

export function SketchLine({ from, to }: { from: Point; to: Point }) {
  const random = seeded(`line:${from.join(",")}:${to.join(",")}`);
  return <path d={roughLine(from, to, random)} {...PEN} />;
}

/* ---------------------------------------------------------------- */
/*  Before and after                                                  */
/* ---------------------------------------------------------------- */

/**
 * The shape of most "what changed" stories: several separate pieces of work
 * before, one connected piece after. Lays itself out from the item count,
 * drawn twice: three columns for wide screens and two, with larger writing,
 * for phones.
 */
export function BeforeAfterSketch({
  number,
  caption,
  before,
  after,
  description,
}: {
  number?: string;
  caption: string;
  before: { label: string; items: string[] };
  after: { label: string; item: string };
  /** What a screen reader hears in place of the drawing. */
  description: string;
}) {
  const shared = { caption, before, after, description };
  return (
    <SketchFigure number={number} caption={caption}>
      <ResponsiveSketch
        wide={<BeforeAfterCanvas {...shared} layout={{ id: "wide", width: 640, columns: 3, heading: 34, label: 24, box: 56, gap: 18 }} />}
        narrow={<BeforeAfterCanvas {...shared} layout={{ id: "narrow", width: 360, columns: 2, heading: 32, label: 22, box: 50, gap: 12 }} />}
      />
    </SketchFigure>
  );
}

/** Two drawings of one figure: `wide` from 640px, `narrow` (larger writing,
 *  fewer columns) on phones. Only the visible one is announced. */
export function ResponsiveSketch({ wide, narrow }: { wide: ReactNode; narrow: ReactNode }) {
  return (
    <>
      <div className={styles.wide}>{wide}</div>
      <div className={styles.narrow}>{narrow}</div>
    </>
  );
}

function BeforeAfterCanvas({
  caption,
  before,
  after,
  description,
  layout,
}: {
  caption: string;
  before: { label: string; items: string[] };
  after: { label: string; item: string };
  description: string;
  layout: { id: string; width: number; columns: number; heading: number; label: number; box: number; gap: number };
}) {
  const { width, heading, label, box, gap } = layout;
  const pad = 16;
  const columns = Math.min(layout.columns, before.items.length);
  const rows = Math.ceil(before.items.length / columns);
  const boxWidth = (width - pad * 2 - gap * (columns - 1)) / columns;
  const gridTop = heading * 2.6;
  const gridBottom = gridTop + rows * box + (rows - 1) * gap;
  const afterLabelY = gridBottom + heading * 2.6;
  const afterTop = afterLabelY + heading * 1.1;
  const height = afterTop + box + 16;

  return (
    <SketchCanvas id={`${caption}-${layout.id}`} width={width} height={height} title={caption} description={description}>
      <SketchLabel x={pad + 8} y={heading * 1.5} circled size={heading}>
        {before.label}
      </SketchLabel>
      {before.items.map((item, index) => (
        <SketchBox
          key={item}
          x={pad + (index % columns) * (boxWidth + gap)}
          y={gridTop + Math.floor(index / columns) * (box + gap)}
          width={boxWidth}
          height={box}
          label={item}
          size={label}
          hatched
        />
      ))}
      <SketchLabel x={pad + 8} y={afterLabelY} circled size={heading}>
        {after.label}
      </SketchLabel>
      <SketchArrow from={[width / 2 - 10, gridBottom + 14]} to={[width / 2 + 14, afterTop - 10]} curve={0.22} />
      <SketchBox x={pad} y={afterTop} width={width - pad * 2} height={box} label={after.item} size={label + 4} />
    </SketchCanvas>
  );
}

/* ---------------------------------------------------------------- */
/*  Notes in running text                                             */
/* ---------------------------------------------------------------- */

/**
 * A handwritten aside against a block of text: a bracket and note in the
 * right margin where there is room, the note under the block where there
 * is not. `hang` picks when the margin is wide enough: "lg" for case-study
 * columns, "xl" for the wider article layout.
 */
export function MarginNote({
  note,
  children,
  hang = "xl",
}: {
  note: string;
  children: ReactNode;
  hang?: "lg" | "xl";
}) {
  const random = seeded(`note:${note}`);
  return (
    <div
      data-margin-note=""
      className={`${ROOT} ${styles.marginNote} ${hang === "lg" ? styles.hangLg : styles.hangXl}`}
    >
      {children}
      <div className={styles.aside} role="note">
        <svg className={styles.bracket} viewBox="0 0 24 100" preserveAspectRatio="none" aria-hidden="true">
          <path d={roughBracket(random)} {...PEN} vectorEffect="non-scaling-stroke" />
        </svg>
        <svg className={styles.arrowUp} viewBox="0 0 26 26" aria-hidden="true">
          <path d={roughArrow([6, 24], [16, 4], random, 0.2)} {...PEN} />
        </svg>
        <p className={`${styles.hand} ${styles.noteText}`}>{note}</p>
      </div>
    </div>
  );
}

/** A loop drawn round a word or figure in running text. */
export function Circled({ children, className }: { children: ReactNode; className?: string }) {
  const random = seeded(`circled:${String(children)}`);
  return (
    <span className={`${ROOT} ${styles.circled} ${className ?? ""}`}>
      {children}
      <svg className={styles.circle} viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true">
        <path d={roughEllipse(50, 20, 47, 17, random)} {...PEN} vectorEffect="non-scaling-stroke" />
      </svg>
    </span>
  );
}

/** A quick pen underline beneath a word or short phrase. It is one stroke
 *  under an inline block, so keep it to words that fit on one line. */
export function Underlined({ children }: { children: ReactNode }) {
  const random = seeded(`underlined:${String(children)}`);
  return (
    <span className={`${ROOT} ${styles.underlined}`}>
      {children}
      <svg className={styles.underline} viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true">
        <path d={roughUnderline(100, random)} {...PEN} vectorEffect="non-scaling-stroke" />
      </svg>
    </span>
  );
}

/** A pen line through a word or short phrase. Renders as <s>, so the
 *  crossing-out keeps its meaning without the browser's own line. */
export function Struck({ children }: { children: ReactNode }) {
  const random = seeded(`struck:${String(children)}`);
  return (
    <s className={`${ROOT} ${styles.struck}`}>
      {children}
      <svg className={styles.strike} viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true">
        <path d={roughUnderline(100, random)} {...PEN} vectorEffect="non-scaling-stroke" />
      </svg>
    </s>
  );
}

/** Handwriting for a short line outside a figure, such as a metric's note. */
export function HandNote({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={`${ROOT} ${styles.hand} ${className ?? ""}`}>{children}</span>;
}

type CalloutArrow = "left" | "right" | "down-left" | "down-right" | "up-left";

/* Arrow paths in a 96 x 64 box, drawn from the note toward the target. */
const CALLOUT_PATHS: Record<CalloutArrow, [Point, Point, number]> = {
  left: [[90, 30], [6, 36], 0.22],
  right: [[6, 30], [90, 36], -0.22],
  "down-left": [[70, 4], [12, 60], 0.28],
  "down-right": [[26, 4], [84, 60], -0.28],
  "up-left": [[70, 60], [12, 6], -0.28],
};

/**
 * A handwritten note with an arrow at something on the page: a product
 * exhibit, a step, a figure. Decorative: the arrow is hidden from screen
 * readers and the note reads as plain text in the flow.
 */
export function SketchCallout({
  note,
  arrow,
  className,
}: {
  note: string;
  arrow: CalloutArrow;
  className?: string;
}) {
  const [from, to, curve] = CALLOUT_PATHS[arrow];
  const random = seeded(`callout:${note}:${arrow}`);
  return (
    <span className={`${ROOT} ${styles.callout} ${className ?? ""}`} data-arrow={arrow}>
      <svg className={styles.calloutArrow} viewBox="0 0 96 64" aria-hidden="true">
        <path d={roughArrow(from, to, random, curve)} {...PEN} />
      </svg>
      <span className={styles.calloutText}>{note}</span>
    </span>
  );
}
